import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  CheckCircle2,
  CircleAlert,
  Loader2,
  RefreshCw,
  Save,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import {
  investorService,
  type InvestorAccountEnvelope,
  type InvestorCompanyUpdatePayload,
  type InvestorProfileUpdatePayload,
} from '@/services/investorService';
import { Loading, ErrorState, UnavailableState } from '@/components/eds';
import {
  FormWizard,
  type WizardStepDef,
  type WizardReviewSection,
  type WizardSubmitState,
} from '@/components/portal/forms/FormWizard';
import OrganisationGovernanceSummary from './OrganisationGovernanceSummary';

const INVESTOR_TYPE_LABEL: Record<string, string> = {
  individual: 'Individual', corporate: 'Corporate', institutional: 'Institutional',
};

// WP2.2 I-09: classify a thrown geo-api error message so 403 is treated as
// Restricted (permission), 401 as session/auth — never "Offline". The service
// layer is frozen (out of WP2.2 scope), so classification is message-based.
const isRestrictedError = (msg: string): boolean => /\b403\b|forbidden|permission|restricted/i.test(msg);
const isAuthError = (msg: string): boolean => /\b401\b|unauthori|session expired/i.test(msg);

type SaveState =
  | { type: 'success'; message: string }
  | { type: 'error'; message: string }
  | null;

const inputCls =
  'w-full px-3 py-2 border border-eds-border rounded-eds-md text-sm bg-eds-surface-2 text-eds-text ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-emerald/40 disabled:bg-eds-surface-2';

const labelCls =
  'block text-xs font-semibold text-eds-fg uppercase tracking-wider mb-1';

const emptyAccount: InvestorAccountEnvelope = {
  investor: null,
  company: null,
};

// Accept any object shape (the service payload interfaces have no index
// signature); Object.entries works structurally and the result is still T.
const cleanPayload = <T extends object>(payload: T): T =>
  Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ])
  ) as T;

const readableStatus = (value?: string | null) =>
  value
    ? value
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Pending';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required = false, children }) => (
  <label className="block">
    <span className={labelCls}>
      {label}
      {required && (
        <span className="ml-1 text-eds-danger" aria-hidden="true">
          *
        </span>
      )}
    </span>
    {children}
  </label>
);

const SaveNotice: React.FC<{ state: SaveState }> = ({ state }) => {
  if (!state) return null;

  return (
    <div
      role={state.type === 'error' ? 'alert' : 'status'}
      className={`text-sm px-3 py-2 rounded-eds-md border ${
        state.type === 'success'
          ? 'bg-eds-success/10 border-eds-success/30 text-eds-success'
          : 'bg-eds-danger/10 border-eds-danger/30 text-eds-danger'
      }`}
    >
      {state.message}
    </div>
  );
};

const InvestorProfileCentre: React.FC = () => {
  const [account, setAccount] =
    useState<InvestorAccountEnvelope>(emptyAccount);
  const [loading, setLoading] = useState(true);
  const [companySaving, setCompanySaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadRestricted, setLoadRestricted] = useState(false);
  // Personal profile now drives the unified wizard (WP2.2-R3); organisation
  // keeps its independent single-page save + SaveNotice.
  const [profileWizard, setProfileWizard] = useState<WizardSubmitState>({ phase: 'idle' });
  const [companyState, setCompanyState] = useState<SaveState>(null);

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setLoadRestricted(false);

    try {
      const result = await investorService.getInvestorAccount();
      setAccount(result);
    } catch (error) {
      // WP2.2 I-09: friendly, request-ID-safe message — never a raw backend
      // object. 403 → Restricted (in-place), 401 → session expiry; never Offline.
      const raw = error instanceof Error ? error.message : '';
      const restricted = isRestrictedError(raw);
      setLoadRestricted(restricted);
      setLoadError(
        restricted
          ? 'You do not currently have permission to view this profile. If you believe this is an error, contact a GIISDEC administrator.'
          : isAuthError(raw)
            ? 'Your session has expired. Please sign in again to view your profile.'
            : 'We could not load your investor profile right now. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  const investor = account.investor;
  const company = account.company;

  const organisationName = useMemo(
    () =>
      company?.legal_name ||
      company?.name ||
      company?.organization_name ||
      company?.trading_name ||
      'Not provided',
    [
      company?.legal_name,
      company?.name,
      company?.organization_name,
      company?.trading_name,
    ]
  );


  const profileRequirements = useMemo(
    () => [
      {
        key: 'first_name',
        label: 'First name',
        complete: Boolean(investor?.first_name?.trim()),
      },
      {
        key: 'last_name',
        label: 'Last name',
        complete: Boolean(investor?.last_name?.trim()),
      },
      {
        key: 'phone',
        label: 'Phone',
        complete: Boolean(investor?.phone?.trim()),
      },
      {
        key: 'nationality',
        label: 'Nationality',
        complete: Boolean(investor?.nationality?.trim()),
      },
      {
        key: 'id_type',
        label: 'Identification type',
        complete: Boolean(investor?.id_type?.trim()),
      },
      {
        key: 'id_number',
        label: 'Identification number',
        complete: Boolean(investor?.id_number?.trim()),
      },
      {
        key: 'investor_type',
        label: 'Investor type',
        complete: Boolean(investor?.investor_type?.trim()),
      },
      {
        key: 'country',
        label: 'Country',
        complete: Boolean(investor?.country?.trim()),
      },
      {
        key: 'city',
        label: 'City',
        complete: Boolean(investor?.city?.trim()),
      },
      {
        key: 'address_line1',
        label: 'Primary address',
        complete: Boolean(investor?.address_line1?.trim()),
      },
    ],
    [investor]
  );

  const profileCompletion = Math.round(
    (profileRequirements.filter((item) => item.complete).length /
      profileRequirements.length) *
      100
  );

  const missingProfileRequirements = profileRequirements.filter(
    (item) => !item.complete
  );

  // Wizard submit handler. Payload construction, validation rules, the geo-api
  // PATCH call and the server-issues[] mapping are UNCHANGED from the prior
  // form; only the signature (form element) and the state channel (wizard) differ.
  const saveProfile = async (formElement: HTMLFormElement) => {
    setProfileWizard({ phase: 'submitting' });

    const form = new FormData(formElement);

    const payload = cleanPayload<InvestorProfileUpdatePayload>({
      first_name: String(form.get('first_name') || ''),
      last_name: String(form.get('last_name') || ''),
      phone: String(form.get('phone') || ''),
      nationality: String(form.get('nationality') || ''),
      id_type: String(form.get('id_type') || ''),
      id_number: String(form.get('id_number') || ''),
      address_line1: String(form.get('address_line1') || ''),
      address_line2: String(form.get('address_line2') || ''),
      city: String(form.get('city') || ''),
      country: String(form.get('country') || ''),
      investor_type: String(form.get('investor_type') || ''),
    });

    const validationIssues: string[] = [];

    if (!payload.first_name) validationIssues.push('First name is required.');
    if (!payload.last_name) validationIssues.push('Last name is required.');
    if (!payload.phone) validationIssues.push('Phone is required.');
    if (!payload.nationality) validationIssues.push('Nationality is required.');
    if (!payload.id_type) validationIssues.push('Identification type is required.');
    if (!payload.id_number) validationIssues.push('Identification number is required.');
    if (
      !payload.investor_type ||
      !['individual', 'corporate', 'institutional'].includes(payload.investor_type)
    ) {
      validationIssues.push('Investor type must be Individual, Corporate, or Institutional.');
    }

    if (validationIssues.length > 0) {
      // Wizard per-step native validation normally prevents this; retained as a
      // defence-in-depth guard preserving the original messages.
      setProfileWizard({ phase: 'error', message: validationIssues.join(' ') });
      return;
    }

    try {
      const response = await investorService.updateInvestorProfile(payload);

      if (response.error) {
        const rawError = response.error as unknown;
        const errorObject =
          typeof rawError === 'object' && rawError !== null
            ? (rawError as {
                message?: string;
                details?: { issues?: Array<{ path?: string; message?: string }> };
              })
            : null;

        const issues = errorObject?.details?.issues
          ?.map((issue) => issue.message)
          .filter((message): message is string => Boolean(message));

        throw new Error(
          issues?.length ? issues.join(' ') : errorObject?.message || String(rawError)
        );
      }

      setProfileWizard({ phase: 'success', message: 'Personal profile saved successfully.' });
      await loadAccount();
    } catch (error) {
      setProfileWizard({
        phase: 'error',
        message: error instanceof Error ? error.message : 'Personal profile could not be saved.',
      });
    }
  };

  // Personal-profile wizard steps — same field names / defaults / required
  // markers as before, regrouped into logical steps. All inputs stay mounted.
  const profileSteps: WizardStepDef[] = [
    {
      key: 'identity', label: 'Identity',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="First name" required><input name="first_name" className={inputCls} defaultValue={investor?.first_name || ''} required /></Field>
          <Field label="Last name" required><input name="last_name" className={inputCls} defaultValue={investor?.last_name || ''} required /></Field>
          <Field label="Phone" required><input name="phone" type="tel" className={inputCls} defaultValue={investor?.phone || ''} required /></Field>
          <Field label="Nationality" required><input name="nationality" className={inputCls} defaultValue={investor?.nationality || ''} required /></Field>
        </div>
      ),
    },
    {
      key: 'identification', label: 'Identification',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="ID type" required>
            <select name="id_type" className={inputCls} defaultValue={investor?.id_type || ''} required>
              <option value="">Select identification type</option>
              <option value="Ghana Card">Ghana Card</option>
              <option value="Passport">Passport</option>
              <option value="Driver's Licence">Driver&apos;s Licence</option>
              <option value="Voter ID">Voter ID</option>
              <option value="Business Registration">Business Registration</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          <Field label="ID number" required><input name="id_number" className={inputCls} defaultValue={investor?.id_number || ''} required /></Field>
          <Field label="Investor type" required>
            <select name="investor_type" className={inputCls} defaultValue={investor?.investor_type || ''} required>
              <option value="">Select investor type</option>
              <option value="individual">Individual</option>
              <option value="corporate">Corporate</option>
              <option value="institutional">Institutional</option>
            </select>
          </Field>
        </div>
      ),
    },
    {
      key: 'address', label: 'Address',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Country"><input name="country" className={inputCls} defaultValue={investor?.country || ''} /></Field>
          <Field label="City"><input name="city" className={inputCls} defaultValue={investor?.city || ''} /></Field>
          <Field label="Address line 1"><input name="address_line1" className={inputCls} defaultValue={investor?.address_line1 || ''} /></Field>
          <Field label="Address line 2"><input name="address_line2" className={inputCls} defaultValue={investor?.address_line2 || ''} /></Field>
        </div>
      ),
    },
  ];

  const buildProfileReview = (v: Record<string, string>): WizardReviewSection[] => [
    { stepKey: 'identity', title: 'Identity', rows: [
      { label: 'First name', value: v.first_name }, { label: 'Last name', value: v.last_name },
      { label: 'Phone', value: v.phone }, { label: 'Nationality', value: v.nationality },
    ] },
    { stepKey: 'identification', title: 'Identification', rows: [
      { label: 'ID type', value: v.id_type }, { label: 'ID number', value: v.id_number },
      { label: 'Investor type', value: INVESTOR_TYPE_LABEL[v.investor_type || ''] || v.investor_type },
    ] },
    { stepKey: 'address', title: 'Address', rows: [
      { label: 'Country', value: v.country }, { label: 'City', value: v.city },
      { label: 'Address line 1', value: v.address_line1 }, { label: 'Address line 2', value: v.address_line2 },
    ] },
  ];

  const saveCompany = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCompanySaving(true);
    setCompanyState(null);

    const form = new FormData(event.currentTarget);

    const payload = cleanPayload<InvestorCompanyUpdatePayload>({
      legal_name: String(form.get('legal_name') || ''),
      trading_name: String(form.get('trading_name') || ''),
      registration_number: String(form.get('registration_number') || ''),
      tax_id: String(form.get('tax_id') || ''),
      incorporation_country: String(
        form.get('incorporation_country') || ''
      ),
      incorporation_date: String(form.get('incorporation_date') || ''),
      sector: String(form.get('sector') || ''),
      sub_sector: String(form.get('sub_sector') || ''),
      website: String(form.get('website') || ''),
      contact_email: String(form.get('contact_email') || ''),
      contact_phone: String(form.get('contact_phone') || ''),
      headquarters_address: String(
        form.get('headquarters_address') || ''
      ),
    });

    try {
      const response = await investorService.updateInvestorCompany(payload);

      if (response.error) {
        throw new Error(String(response.error));
      }

      setCompanyState({
        type: 'success',
        message: 'Organisation information saved successfully.',
      });

      await loadAccount();
    } catch (error) {
      setCompanyState({
        type: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Organisation information could not be saved.',
      });
    } finally {
      setCompanySaving(false);
    }
  };

  if (loading) {
    // WP2.2 I-08: frozen EDS governed Loading state.
    return <div className="py-6"><Loading label="Loading investor profile…" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <UserRound className="w-5 h-5 text-eds-success" />
            <h2 className="text-lg font-semibold text-eds-fg">
              Investor Profile Centre
            </h2>
          </div>
          <p className="text-sm text-eds-muted-fg mt-1">
            Maintain your personal and organisation information used across
            GIISDEC investor workflows.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void loadAccount()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-eds-md border border-eds-border text-eds-fg hover:bg-eds-surface-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loadError && (
        // WP2.2 I-08/I-09: governed EDS state — Restricted (403) rendered
        // in-place as Unavailable/permission (never Offline); other failures as
        // a friendly retryable Error. No raw backend object is ever shown.
        loadRestricted ? (
          <UnavailableState
            phase="Permission required"
            title="Restricted"
            message={loadError}
            action={
              <button type="button" onClick={() => void loadAccount()} className="text-sm font-semibold text-eds-emerald-2 hover:underline">
                Try again
              </button>
            }
          />
        ) : (
          <ErrorState title="Profile unavailable" message={loadError} onRetry={() => void loadAccount()} />
        )
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border border-eds-border rounded-eds-md p-4 bg-eds-surface-2">
          <div className="text-xs uppercase tracking-wider text-eds-muted-fg">
            Investor status
          </div>
          <div className="mt-1 font-semibold text-eds-fg">
            {readableStatus(investor?.status)}
          </div>
        </div>

        <div className="border border-eds-border rounded-eds-md p-4 bg-eds-surface-2">
          <div className="text-xs uppercase tracking-wider text-eds-muted-fg">
            Organisation
          </div>
          <div className="mt-1 font-semibold text-eds-fg">
            {organisationName}
          </div>
        </div>

        <div className="border border-eds-border rounded-eds-md p-4 bg-eds-surface-2">
          <div className="text-xs uppercase tracking-wider text-eds-muted-fg">
            Registration
          </div>
          <div className="mt-1 font-semibold text-eds-fg">
            {company?.registration_number || 'Not provided'}
          </div>
        </div>

        <div className="border border-eds-border rounded-eds-md p-4 bg-eds-surface-2">
          <div className="text-xs uppercase tracking-wider text-eds-muted-fg">
            Last updated
          </div>
          <div className="mt-1 font-semibold text-eds-fg">
            {formatDate(investor?.updated_at || company?.updated_at)}
          </div>
        </div>
      </section>


      <OrganisationGovernanceSummary account={account} />

      {/* Profile completion meter — display-only context above the wizard. */}
      <div className="space-y-5">
        <div
          className={`rounded-eds-md border px-4 py-4 ${
            profileCompletion === 100
              ? 'border-eds-success/30 bg-eds-success/10'
              : 'border-eds-warning/30 bg-eds-warning/10'
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                {profileCompletion === 100 ? (
                  <CheckCircle2 className="w-5 h-5 text-eds-success" />
                ) : (
                  <CircleAlert className="w-5 h-5 text-eds-warning" />
                )}
                <h4 className="font-semibold text-eds-fg">
                  {profileCompletion === 100
                    ? 'Investor profile complete'
                    : 'Complete your investor profile'}
                </h4>
              </div>

              <p className="mt-1 text-sm text-eds-fg">
                Complete all required identity fields before submitting or
                progressing regulated investor applications.
              </p>
            </div>

            <div className="text-right">
              <div className="text-2xl font-semibold text-eds-fg">
                {profileCompletion}%
              </div>
              <div className="text-xs uppercase tracking-wider text-eds-muted-fg">
                Profile completion
              </div>
            </div>
          </div>

          <div className="mt-4 h-2 overflow-hidden rounded-eds-pill bg-eds-surface">
            <div
              className={`h-full rounded-eds-pill transition-all ${
                profileCompletion === 100
                  ? 'bg-eds-success'
                  : 'bg-eds-warning'
              }`}
              style={{ width: `${profileCompletion}%` }}
            />
          </div>

          {missingProfileRequirements.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                Remaining required information
              </div>

              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {missingProfileRequirements.map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center gap-2 rounded-eds-md border border-eds-warning/30 bg-eds-surface px-3 py-2 text-sm text-eds-fg"
                  >
                    <CircleAlert className="w-4 h-4 shrink-0 text-eds-warning" />
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>


      </div>
      <FormWizard
        title="Personal Information"
        description="Details associated with your authenticated investor account."
        steps={profileSteps}
        buildReview={buildProfileReview}
        submitLabel="Save Personal Profile"
        submitState={profileWizard}
        onSubmit={saveProfile}
      />

      <form
        key={`company-${company?.updated_at || 'new'}`}
        onSubmit={saveCompany}
        className="border border-eds-border rounded-eds-lg p-5 space-y-5"
      >
        <div className="flex items-center gap-2 border-b border-eds-border pb-3">
          <Building2 className="w-4 h-4 text-eds-success" />
          <div>
            <h3 className="font-semibold text-eds-fg">
              Organisation Information
            </h3>
            <p className="text-sm font-medium text-eds-muted-fg">
              Corporate information used for validation, agreements and
              investment applications.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Legal name">
            <input
              name="legal_name"
              className={inputCls}
              defaultValue={
                company?.legal_name ||
                company?.name ||
                company?.organization_name ||
                ''
              }
              required
            />
          </Field>

          <Field label="Trading name">
            <input
              name="trading_name"
              className={inputCls}
              defaultValue={company?.trading_name || ''}
            />
          </Field>

          <Field label="Registration number">
            <input
              name="registration_number"
              className={inputCls}
              defaultValue={company?.registration_number || ''}
            />
          </Field>

          <Field label="Tax ID">
            <input
              name="tax_id"
              className={inputCls}
              defaultValue={company?.tax_id || ''}
            />
          </Field>

          <Field label="Incorporation country">
            <input
              name="incorporation_country"
              className={inputCls}
              defaultValue={company?.incorporation_country || ''}
            />
          </Field>

          <Field label="Incorporation date">
            <input
              name="incorporation_date"
              type="date"
              className={inputCls}
              defaultValue={
                company?.incorporation_date
                  ? company.incorporation_date.slice(0, 10)
                  : ''
              }
            />
          </Field>

          <Field label="Sector">
            <input
              name="sector"
              className={inputCls}
              defaultValue={company?.sector || ''}
            />
          </Field>

          <Field label="Sub-sector">
            <input
              name="sub_sector"
              className={inputCls}
              defaultValue={company?.sub_sector || ''}
            />
          </Field>

          <Field label="Website">
            <input
              name="website"
              type="url"
              className={inputCls}
              defaultValue={company?.website || ''}
              placeholder="https://"
            />
          </Field>

          <Field label="Contact email">
            <input
              name="contact_email"
              type="email"
              className={inputCls}
              defaultValue={company?.contact_email || ''}
            />
          </Field>

          <Field label="Contact phone">
            <input
              name="contact_phone"
              type="tel"
              className={inputCls}
              defaultValue={company?.contact_phone || ''}
            />
          </Field>

          <div className="md:col-span-2">
            <Field label="Headquarters address">
              <textarea
                name="headquarters_address"
                rows={3}
                className={inputCls}
                defaultValue={company?.headquarters_address || ''}
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={companySaving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-eds-success hover:opacity-90 disabled:opacity-60 text-white text-sm font-medium rounded-eds-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
          >
            {companySaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {companySaving ? 'Saving…' : 'Save Organisation'}
          </button>

          <SaveNotice state={companyState} />
        </div>
      </form>

      <div className="flex items-start gap-3 p-4 border border-eds-success/30 bg-eds-success/10 rounded-eds-md">
        <ShieldCheck className="w-5 h-5 text-eds-success shrink-0 mt-0.5" />
        <div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-eds-success" />
            <p className="text-sm font-medium text-eds-fg">
              Server-authoritative profile management
            </p>
          </div>
          <p className="text-xs text-eds-fg mt-1">
            Profile changes are processed through geo-api, protected by your
            authenticated session and retained under GIISDEC audit controls.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvestorProfileCentre;
